import { useState, useEffect } from "react"
import {
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Col,
  Label,
  Input,
  FormFeedback,
  Button,
  Form,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"

import { useEditVehicleStock } from "hooks/stockHook"
import ButtonTooltip from "components/Common/ButtonTooltip"
import { useToastMessage } from "hooks/useToastMessage"

// Custom Hooks
import { useVehicleForm } from "hooks/useVehicleForm"
import FormikCreatableField from "components/Common/FormikCreatableField"

export default function StockSpecificationsUpdate({ stock = null }) {
  const [modal, setModal] = useState(false)

  const toggle = () => {
    if (modal) {
      // When closing, reset form to initial values
      formik.resetForm()
    }
    setModal(!modal)
  }

  const { mutateAsync: editStock, isPending: stockEditLoading } =
    useEditVehicleStock()

  const { handleResponse } = useToastMessage()

  const validationSchema = Yup.object({
    vs_brand_id: Yup.string().nullable(),
    vs_model_id: Yup.string().nullable(),
    vs_year_id: Yup.string().nullable(),
    vs_package_id: Yup.string().nullable(),
    vs_ex_color_id: Yup.string().nullable(),
    vs_int_color_id: Yup.string().nullable(),
    vs_location_id: Yup.string().nullable(),
    mileage: Yup.number().min(0, "Mileage must be positive").nullable(),
    doors: Yup.number().min(0, "Doors must be positive").nullable(),
    seats: Yup.number().min(0, "Seats must be positive").nullable(),
    tire_size: Yup.string().nullable(),
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      vs_brand_id: stock?.vs_brand_id || "",
      vs_model_id: stock?.vs_model_id || "",
      vs_year_id: stock?.vs_year_id || "",
      vs_package_id: stock?.vs_package_id || "",
      vs_ex_color_id: stock?.vs_ex_color_id || "",
      vs_int_color_id: stock?.vs_int_color_id || "",
      vs_location_id: stock?.vs_location_id || "",
      mileage: stock?.mileage || "",
      doors: stock?.doors || "",
      seats: stock?.seats || "",
      tire_size: stock?.tire_size || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { resetForm }) => {
      try {
        const res = await editStock({
          id: stock?.id,
          isResetForModel: true,
          ...values,
        })
        handleResponse(res, "Specifications updated successfully.", true)
        if (res.success) {
          toggle()
          resetForm()
        }
      } catch (error) {
        console.error("Failed to update specifications:", error)
      }
    },
  })

  // handle set initial value of model & package
  useEffect(() => {
    if (modal) {
      formik.setFieldValue("vs_model_id", stock?.vs_model_id)
      formik.setFieldValue("vs_package_id", stock?.vs_package_id)
    }
  }, [stock?.vs_model_id, stock?.vs_package_id, modal])

  const {
    handleBrandChange,
    handleModelChange,
    optionLoading,
    brandsData,
    modelsData,
    packagesData,
    yearsData,
    extColorsData,
    intColorsData,
    locationsData,
  } = useVehicleForm({ stockResponse: stock, isEdit: true, formik })

  // Reset model and model-associated fields when brand changes
  useEffect(() => {
    if (
      formik.values.vs_brand_id &&
      formik.values.vs_brand_id !== stock?.vs_brand_id
    ) {
      formik.setValues(
        prev => ({
          ...prev,
          vs_model_id: "",
          vs_package_id: "",
        }),
        false
      )
    }
  }, [formik.values.vs_brand_id, stock?.vs_brand_id])

  // Reset model-associated fields when model changes
  useEffect(() => {
    if (
      formik.values.vs_model_id &&
      formik.values.vs_model_id !== stock?.vs_model_id
    ) {
      formik.setValues(
        prev => ({
          ...prev,
          vs_package_id: "",
        }),
        false
      )
    }
  }, [formik.values.vs_model_id, stock?.vs_model_id])

  return (
    <>
      <ButtonTooltip
        type="edit"
        onClick={toggle}
        btnId="edit-specifications"
        btnName="Edit Specifications"
      />

      {modal && (
        <Modal isOpen={modal} toggle={toggle} centered size="lg">
          <ModalHeader toggle={toggle}>Edit Specifications</ModalHeader>
          <ModalBody>
            <Form
              onSubmit={e => {
                e.preventDefault()
                formik.handleSubmit()
              }}
            >
              <Row>
                {/* Brand */}
                <Col md={6}>
                  <FormikCreatableField
                    name="vs_brand_id"
                    label="Brand"
                    formik={formik}
                    data={brandsData}
                    onChange={handleBrandChange}
                    placeholder="Select Brand"
                    isLoading={optionLoading}
                  />
                  {formik.touched.vs_brand_id && formik.errors.vs_brand_id && (
                    <FormFeedback>{formik.errors.vs_brand_id}</FormFeedback>
                  )}
                </Col>

                {/* Model */}
                <Col md={6}>
                  <FormikCreatableField
                    name="vs_model_id"
                    label="Model"
                    formik={formik}
                    data={modelsData}
                    onChange={handleModelChange}
                    placeholder="Select Model"
                    isLoading={optionLoading || !formik.values.vs_brand_id}
                  />
                  {formik.touched.vs_model_id && formik.errors.vs_model_id && (
                    <FormFeedback>{formik.errors.vs_model_id}</FormFeedback>
                  )}
                </Col>

                {/* Year */}
                <Col md={6}>
                  <FormikCreatableField
                    name="vs_year_id"
                    label="Year"
                    formik={formik}
                    data={yearsData}
                    onChange={formik.handleChange}
                    placeholder="Select Year"
                    isLoading={optionLoading}
                  />
                  {formik.touched.vs_year_id && formik.errors.vs_year_id && (
                    <FormFeedback>{formik.errors.vs_year_id}</FormFeedback>
                  )}
                </Col>

                {/* Package */}
                <Col md={6}>
                  <FormikCreatableField
                    name="vs_package_id"
                    label="Package"
                    formik={formik}
                    data={packagesData}
                    onChange={formik.handleChange}
                    placeholder="Select Package"
                    isLoading={optionLoading}
                  />
                  {formik.touched.vs_package_id &&
                    formik.errors.vs_package_id && (
                      <FormFeedback>{formik.errors.vs_package_id}</FormFeedback>
                    )}
                </Col>

                {/* Exterior Color */}
                <Col md={6}>
                  <FormikCreatableField
                    name="vs_ex_color_id"
                    label="Exterior Color"
                    formik={formik}
                    data={extColorsData}
                    onChange={formik.handleChange}
                    placeholder="Select Color"
                    isLoading={optionLoading}
                  />
                  {formik.touched.vs_ex_color_id &&
                    formik.errors.vs_ex_color_id && (
                      <FormFeedback>
                        {formik.errors.vs_ex_color_id}
                      </FormFeedback>
                    )}
                </Col>

                {/* Interior Color */}
                <Col md={6}>
                  <FormikCreatableField
                    name="vs_int_color_id"
                    label="Interior Color"
                    formik={formik}
                    data={intColorsData}
                    onChange={formik.handleChange}
                    placeholder="Select Color"
                    isLoading={optionLoading}
                  />
                  {formik.touched.vs_int_color_id &&
                    formik.errors.vs_int_color_id && (
                      <FormFeedback>
                        {formik.errors.vs_int_color_id}
                      </FormFeedback>
                    )}
                </Col>

                {/* Location */}
                <Col md={6}>
                  <FormikCreatableField
                    name="vs_location_id"
                    label="Location"
                    formik={formik}
                    data={locationsData}
                    onChange={formik.handleChange}
                    placeholder="Select Location"
                    isLoading={optionLoading}
                  />
                  {formik.touched.vs_location_id &&
                    formik.errors.vs_location_id && (
                      <FormFeedback>
                        {formik.errors.vs_location_id}
                      </FormFeedback>
                    )}
                </Col>

                {/* Mileage */}
                <Col md={6} className="mb-3">
                  <Label htmlFor="mileage">Mileage (km)</Label>
                  <Input
                    type="number"
                    name="mileage"
                    value={formik.values.mileage}
                    onChange={formik.handleChange}
                  />
                  {formik.touched.mileage && formik.errors.mileage && (
                    <FormFeedback>{formik.errors.mileage}</FormFeedback>
                  )}
                </Col>

                {/* Doors */}
                <Col md={6} className="mb-3">
                  <Label htmlFor="doors">Doors</Label>
                  <Input
                    type="number"
                    name="doors"
                    value={formik.values.doors}
                    onChange={formik.handleChange}
                  />
                  {formik.touched.doors && formik.errors.doors && (
                    <FormFeedback>{formik.errors.doors}</FormFeedback>
                  )}
                </Col>

                {/* Seats */}
                <Col md={6} className="mb-3">
                  <Label htmlFor="seats">Seats</Label>
                  <Input
                    type="number"
                    name="seats"
                    value={formik.values.seats}
                    onChange={formik.handleChange}
                  />
                  {formik.touched.seats && formik.errors.seats && (
                    <FormFeedback>{formik.errors.seats}</FormFeedback>
                  )}
                </Col>

                {/* Tire Size */}
                <Col md={6}>
                  <Label htmlFor="tire_size">Tire Size</Label>
                  <Input
                    type="text"
                    name="tire_size"
                    value={formik.values.tire_size}
                    onChange={formik.handleChange}
                  />
                  {formik.touched.tire_size && formik.errors.tire_size && (
                    <FormFeedback>{formik.errors.tire_size}</FormFeedback>
                  )}
                </Col>
              </Row>

              <div className="text-end mt-4">
                <Button
                  type="submit"
                  color="success"
                  disabled={stockEditLoading}
                >
                  Save Changes
                  {stockEditLoading && "..."}
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>
      )}
    </>
  )
}
